import { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import BrandLogo from '../components/BrandLogo'
import CookieBanner from '../components/CookieBanner'
import { getDataAPI, postDataAPI } from '../lib/api'

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const from = useMemo(() => {
    const s = (location.state as any)?.from
    return typeof s === 'string' && s.startsWith('/') ? s : '/'
  }, [location.state])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const normalized = email.trim().toLowerCase()
      if (!normalized) throw new Error('Email is required')

      // Check if email exists
      const check = await getDataAPI(`auth/checkemail?email=${encodeURIComponent(normalized)}`)
      const exists =
        check?.data?.exist ??
        check?.data?.exists ??
        check?.exist ??
        check?.exists ??
        check?.data?.data?.exist ??
        false

      if (exists) {
        navigate(`/login-otp?email=${encodeURIComponent(normalized)}&from=${encodeURIComponent(from)}`)
        return
      }

      // New user: check if they were invited so signup can show manager onboarding
      let invited = false
      try {
        const invitedRes = await postDataAPI('team/invited-artists', { teamMemberEmail: normalized })
        const raw = invitedRes?.data?.data ?? invitedRes?.data ?? (Array.isArray(invitedRes) ? invitedRes : null)
        const list = Array.isArray(raw) ? raw : []
        invited = list.length > 0
      } catch {
        // Backend may require auth or endpoint may not exist; continue with normal signup
      }

      const params = new URLSearchParams({ email: normalized, from })
      if (invited) params.set('invited', '1')
      navigate(`/signup?${params.toString()}`)
    } catch (err: any) {
      setError(err?.message || 'An error occurred. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Center content */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 bg-[#FF2300] rounded-sm flex-shrink-0"></div>
              <BrandLogo className="h-3 w-auto text-gray-900 dark:text-white" />
            </div>
            <p className="text-sm text-gray-500">Enter your email to sign in or create an account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="ds-input"
              />
            </div>
            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}
            <button
              type="submit"
              disabled={isLoading}
              className="ds-btn ds-btn-primary w-full disabled:opacity-50"
            >
              {isLoading ? 'Continuing...' : 'Continue'}
            </button>
          </form>
        </div>
      </div>

      {/* Footer with legal links */}
      <footer className="py-6 px-8">
        <div className="max-w-md mx-auto flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-gray-500">
          <Link to="/privacy-policy" className="hover:text-gray-900 dark:hover:text-white transition-colors">
            Privacy Policy
          </Link>
          <Link to="/terms-of-service" className="hover:text-gray-900 dark:hover:text-white transition-colors">
            Terms of Service
          </Link>
          <Link to="/accessibility-statement" className="hover:text-gray-900 dark:hover:text-white transition-colors">
            Accessibility
          </Link>
        </div>
        <p className="text-center text-xs text-gray-400 mt-3">© {new Date().getFullYear()} DIZEE. All rights reserved.</p>
      </footer>
      
      {/* Cookie Banner */}
      <CookieBanner />
    </div>
  )
}
